class DroplandProductEnhancements extends HTMLElement {
  connectedCallback() {
    this.relocate();
    this.syncWholesalePricing();
    this.observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => this.relocate());
    });
    const observerTarget = this.ownerDocument?.documentElement;
    if (observerTarget) {
      this.observer.observe(observerTarget, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  elementFor(slot) {
    const id = `dropland-product-${slot}-${this.dataset.sectionId}`;
    let element = document.getElementById(id);
    if (element) return element;

    const template = this.querySelector(`template[data-slot="${slot}"]`);
    if (!template) return null;
    element = template.content.firstElementChild.cloneNode(true);
    element.id = id;
    return element;
  }

  relocate() {
    const root = this.closest('.shopify-section') || document;
    const details = root.querySelector('.product-details');
    const price = details?.querySelector('product-price');
    const buyButtons = details?.querySelector('.buy-buttons-block');
    if (!details || !buyButtons) return;

    const wholesalePricing = this.elementFor('wholesale-pricing');
    if (wholesalePricing && price && wholesalePricing.previousElementSibling !== price) {
      price.insertAdjacentElement('afterend', wholesalePricing);
    }

    const availability = this.elementFor('availability');
    const availabilityAnchor = wholesalePricing || price;
    if (availability && availabilityAnchor && availability.previousElementSibling !== availabilityAnchor) {
      availabilityAnchor.insertAdjacentElement('afterend', availability);
    }

    const variantPicker = details.querySelector('variant-picker');
    if (variantPicker && variantPicker.nextElementSibling !== buyButtons) {
      variantPicker.insertAdjacentElement('afterend', buyButtons);
    }

    const addButton = buyButtons.querySelector('.add-to-cart-button');
    if (addButton) {
      const label = this.dataset.addLabel;
      const labelElement = addButton.querySelector('.add-to-cart-text__content span span');
      if (labelElement && label) labelElement.textContent = label;
    }

    const buyNow = this.elementFor('buy-now');
    const buttonList = buyButtons.querySelector('.product-form-buttons');
    const addToCartComponent = addButton?.closest('add-to-cart-component');
    const addToCartBlock = addToCartComponent?.parentElement;
    if (buyNow && buttonList && addToCartBlock && buyNow.parentElement !== buttonList) {
      buttonList.insertBefore(buyNow, addToCartBlock);
    }
    const quantityControl = buttonList?.querySelector('.quantity-selector-wrapper');
    if (quantityControl && buyNow && quantityControl.parentElement !== buttonList) {
      buttonList.insertBefore(quantityControl, buyNow);
    }

  }

  syncWholesalePricing(quantity) {
    const root = this.closest('.shopify-section') || document;
    const pricing = root.querySelector('.dropland-wholesale-pricing');
    const quantityInput = root.querySelector('quantity-selector-component input[name="quantity"]');
    const selectedQuantity = Number.isFinite(quantity) ? quantity : Number.parseInt(quantityInput?.value, 10);
    if (!pricing || !Number.isFinite(selectedQuantity)) return;

    const tiers = [...pricing.querySelectorAll('[data-tier]')];
    const activeTier = tiers
      .filter((tier) => Number.parseInt(tier.dataset.tier, 10) <= selectedQuantity)
      .at(-1) || tiers[0];

    tiers.forEach((tier) => tier.classList.toggle('is-active', tier === activeTier));
  }

}

if (!customElements.get('dropland-product-enhancements')) {
  customElements.define('dropland-product-enhancements', DroplandProductEnhancements);
}

// The theme's quantity component can retain the initial disabled state of the
// minus button after increasing a product quantity. Reapply the true minimum
// state after its own click handler has finished, without affecting cart rows.
function syncProductQuantityMinus(selector) {
  if (!(selector instanceof HTMLElement) || selector.matches('cart-quantity-selector-component')) return;

  const input = selector.querySelector('input[name="quantity"]');
  const minusButton = selector.querySelector('.quantity-minus');
  if (!input || !minusButton || input.disabled) return;

  window.requestAnimationFrame(() => {
    const minimum = Number.parseInt(input.min, 10) || 1;
    const quantity = Number.parseInt(input.value, 10) || minimum;
    minusButton.disabled = quantity <= minimum;
  });
}

// Keep the native product form aligned with the variant-picker selection.
// The picker updates the product URL, but custom product layouts can retain a
// stale hidden `id` field; Shopify would then add the previous variant.
document.addEventListener('change', (event) => {
  const option = event.target;
  if (!(option instanceof HTMLInputElement || option instanceof HTMLOptionElement)) return;

  const picker = option.closest('variant-picker');
  const variantId = option.dataset.variantId;
  if (!picker || !variantId) return;

  const section = picker.closest('.shopify-section') || document;
  section.querySelectorAll('form.shopify-product-form input[name="id"]').forEach((input) => {
    input.value = variantId;
    input.setAttribute('value', variantId);
  });
});

// Keep the visual volume tier aligned with the native product quantity control.
document.addEventListener('quantity-selector:update', (event) => {
  const selector = event.target;
  if (!(selector instanceof HTMLElement)) return;

  syncProductQuantityMinus(selector);
  const enhancements = selector.closest('.shopify-section')?.querySelector('dropland-product-enhancements');
  enhancements?.syncWholesalePricing(event.detail?.quantity);
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.name !== 'quantity') return;

  syncProductQuantityMinus(input.closest('quantity-selector-component'));
  const enhancements = input.closest('.shopify-section')?.querySelector('dropland-product-enhancements');
  enhancements?.syncWholesalePricing();
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-dropland-buy-now]');
  if (!button || button.disabled) return;

  const productForm = button.closest('.buy-buttons-block')?.querySelector('form') || document.querySelector('product-form-component form');
  if (!productForm) return;

  event.preventDefault();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(productForm),
    });
    if (!response.ok) throw new Error('Unable to add product');
    window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    productForm.requestSubmit?.();
  }
});
